/**
 * Main entry point for the Harmony React application.
 *
 * BOOTSTRAP ORDER MATTERS:
 * 1. Theme service kicks off asynchronously (non-blocking, avoids FOUC).
 * 2. `initApiConfig()` MUST resolve BEFORE the app tree is imported: modules
 *    like lib/rpc-client.ts, lib/auth.ts and services/api.ts capture the API
 *    base URL at import time. Static imports would execute them before any of
 *    our code runs, so the whole provider stack lives in AppRoot.tsx and is
 *    loaded via dynamic import once the runtime server URL is known.
 *    (This is what makes Settings → Serveur work in the Android APK.)
 */

// Load theme variables first, then base styles
import './styles/tailwind.css';
import './styles/base.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { themeService } from './services/themeService';
import { initApiConfig } from './lib/apiConfig';

// Initialize theme service before rendering
// This ensures the correct theme is applied immediately
// Note: initialize() is async but we call it synchronously to avoid FOUC
// The theme will load from IndexedDB and apply before first paint
themeService
  .initialize({
    defaultTheme: 'light',
  })
  .catch((error) => {
    // If IndexedDB fails, default to light mode
    console.warn('Failed to initialize theme from IndexedDB:', error);
    themeService.setTheme('light');
  });

/**
 * Async application bootstrap:
 * resolve the runtime API base URL, THEN import + render the app tree.
 */
async function bootstrap(): Promise<void> {
  await initApiConfig();

  // Dynamic import: everything below transitively creates API clients and
  // therefore requires apiConfig to be initialized first.
  const { AppRoot } = await import('./components/AppRoot');

  const appContainer = document.getElementById('app');

  if (!appContainer) {
    console.error('App container not found');
    return;
  }

  const root = createRoot(appContainer);
  root.render(
    <StrictMode>
      <AppRoot />
    </StrictMode>,
  );
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void bootstrap();
  });
} else {
  void bootstrap();
}
